/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2025 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UUID } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';
import { Dispatch } from '@reduxjs/toolkit';
import { GlobalService } from './global.service';
import { FieldSettingPayload, FieldSettingResponse } from 'interfaces/fieldSetting.interface';

export class FieldSettingService {

  static addFieldSetting(
    payload: FieldSettingPayload,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<{ id: UUID } | null> {
    return new Promise( async resolve => {
      if ( !payload.name ) {
        throw new Error( 'name_is_required' );
      }
      const validTypes = [ 'list', 'int', 'string', 'date' ];
      if ( !validTypes.includes( payload.type ) ) {
        throw new Error( `type must be one of ${validTypes.join( ', ' )}` );
      }
      if ( payload.type === 'string' ) {
        if ( payload.textMaxLength !== undefined && typeof payload.textMaxLength !== 'number' ) {
          throw new Error( 'textMaxLength must be a number when provided for type "string"' );
        }
      } else if ( payload.textMaxLength !== undefined ) {
        throw new Error( 'textMaxLength is only allowed for type "string"' );
      }
      if ( payload.type === 'int' ) {
        if ( ( payload.numberMin !== undefined && typeof payload.numberMin !== 'number' ) ||
             ( payload.numberMax !== undefined && typeof payload.numberMax !== 'number' ) ) {
          throw new Error( 'numberMin and numberMax must be numbers when provided for type "int"' );
        }
      } else {
        if ( payload.numberMin !== undefined || payload.numberMax !== undefined ) {
          throw new Error( 'numberMin and numberMax are only allowed for type "int"' );
        }
      }
      if ( payload.type === 'list' ) {
        if ( payload.listItems !== undefined && ( !Array.isArray( payload.listItems ) || payload.listItems.length === 0 ) ) {
          throw new Error( 'listItems must be a non-empty array when provided for type "list"' );
        }
      } else {
        if ( payload.listItems !== undefined ) {
          throw new Error( 'listItems is only allowed for type "list"' );
        }
      }
      if ( !( payload as FieldSettingPayload ).siteId || typeof ( payload as FieldSettingPayload ).siteId !== 'string' ) {
        throw new Error( 'invalid_or_missing_siteId_in_fieldSetting' );
      }

      const data: Record<string, unknown> = {
        name: payload.name,
        type: payload.type,
        siteId: payload.siteId,
      };
      if ( typeof payload.position === 'number' ) {
        data.position = payload.position;
      }

      if ( payload.type === 'string' && payload.textMaxLength !== undefined ) {
        data.textMaxLength = payload.textMaxLength;
      } else if ( payload.type === 'int' && ( payload.numberMin !== undefined || payload.numberMax !== undefined ) ) {
        if ( payload.numberMin !== undefined ) {data.numberMin = payload.numberMin;}
        if ( payload.numberMax !== undefined ) {data.numberMax = payload.numberMax;}
      } else if ( payload.type === 'list' && payload.listItems !== undefined ) {
        data.listItems = payload.listItems;
      }

      AXIOS.post(
        `${API.FIELD_SETTINGS}`,
        data,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 ? response.data : null );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  static updateFieldSetting(
    settingId: string,
    payload: FieldSettingPayload,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      if ( !payload.name ) {
        throw new Error( 'name_is_required' );
      }
      const validTypes = [ 'list', 'int', 'string', 'date' ];
      if ( !validTypes.includes( payload.type ) ) {
        throw new Error( `type must be one of ${validTypes.join( ', ' )}` );
      }

      if ( payload.type === 'string' ) {
        if ( payload.textMaxLength !== undefined && typeof payload.textMaxLength !== 'number' ) {
          throw new Error( 'textMaxLength must be a number when provided for type "string"' );
        }
      } else if ( payload.textMaxLength !== undefined ) {
        throw new Error( 'textMaxLength is only allowed for type "string"' );
      }
      if ( payload.type === 'int' ) {
        if ( ( payload.numberMin !== undefined && typeof payload.numberMin !== 'number' ) ||
             ( payload.numberMax !== undefined && typeof payload.numberMax !== 'number' ) ) {
          throw new Error( 'numberMin and numberMax must be numbers when provided for type "int"' );
        }
      } else {
        if ( payload.numberMin !== undefined || payload.numberMax !== undefined ) {
          throw new Error( 'numberMin and numberMax are only allowed for type "int"' );
        }
      }
      if ( payload.type === 'list' ) {
        if ( payload.listItems !== undefined && ( !Array.isArray( payload.listItems ) || payload.listItems.length === 0 ) ) {
          throw new Error( 'listItems must be a non-empty array when provided for type "list"' );
        }
      } else {
        if ( payload.listItems !== undefined ) {
          throw new Error( 'listItems is only allowed for type "list"' );
        }
      }
      if ( !( payload as FieldSettingPayload ).siteId || typeof ( payload as FieldSettingPayload ).siteId !== 'string' ) {
        throw new Error( 'invalid_or_missing_siteId_in_fieldSetting' );
      }

      const data: Record<string, unknown> = {
        name: payload.name,
        type: payload.type,
        siteId: payload.siteId,
      };
      if ( typeof payload.position === 'number' ) {
        data.position = payload.position;
      }

      if ( payload.type === 'string' && payload.textMaxLength !== undefined ) {
        data.textMaxLength = payload.textMaxLength;
      } else if ( payload.type === 'int' && ( payload.numberMin !== undefined || payload.numberMax !== undefined ) ) {
        if ( payload.numberMin !== undefined ) {data.numberMin = payload.numberMin;}
        if ( payload.numberMax !== undefined ) {data.numberMax = payload.numberMax;}
      } else if ( payload.type === 'list' && payload.listItems !== undefined ) {
        data.listItems = payload.listItems;
      }

      AXIOS.put(
        `${API.FIELD_SETTINGS}/${settingId}`,
        data,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteFieldSetting(
    settingId: string,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<FieldSettingResponse | undefined> {
    return new Promise( async resolve => {
      AXIOS.delete(
        `${API.FIELD_SETTINGS}/${settingId}`,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getBySiteId(
    siteId: string,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<FieldSettingResponse | undefined> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.FIELD_SETTINGS}/${siteId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static addFieldValueToEvent(
    eventId: string,
    settingId: string,
    value: unknown,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<unknown> {
    return new Promise( async resolve => {
      if ( !eventId || typeof eventId !== 'string' ) {
        throw new Error( 'eventId is required and must be a string' );
      }
      if ( !settingId || typeof settingId !== 'string' ) {
        throw new Error( 'settingId is required and must be a string' );
      }
      const payload = {
        settingId,
        value
      };
      AXIOS.post(
        `${API.EVENTS}/${eventId}/fields`,
        payload,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getFieldValuesByEventId(
    eventId: string,
    token: string | null,
    dispatch: Dispatch,
    onError?: CallbackErrorAxios,
    onWarning?: CallbackWarningAxios
  ): Promise<unknown[] | undefined> {
    return new Promise( async resolve => {
      if ( !eventId || typeof eventId !== 'string' ) {
        throw new Error( 'eventId is required and must be a string' );
      }
      AXIOS.get( `${API.EVENTS}/${eventId}/fields`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

}